import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchQueue, fetchPuzzles, submitAttempt } from '../services/api';
import { track, flush } from '../services/track';
import { useLocalStorage } from '../hooks/useLocalStorage';
import SpatialPuzzle3D from '../components/SpatialPuzzle3D';
import LearningCapsule from '../components/LearningCapsule';
import PracticePuzzle from '../components/PracticePuzzle';
import CategorySortPuzzle from '../components/CategorySortPuzzle';
import PoemPuzzle from '../components/PoemPuzzle';
import NumericPuzzle from '../components/NumericPuzzle';
import OrderingPuzzle from '../components/OrderingPuzzle';
import MultiPuzzle from '../components/MultiPuzzle';
import GuardianGrowth from '../components/GuardianGrowth';
import { speciesEmoji } from '../utils/guardian';
import { getCopy } from '../utils/copy';
import { describeNextReview } from '../utils/reviewTime';
import { medalFor, newRoundId, roundSizeFor } from '../utils/round';

// 闯关队列由后端按六艺书院的规则排好(docs/plan-003):不指定学科时按学段顺序
// 排全部学科(「继续冒险」),指定 ?dimension= 时只排这一门;每个知识点带上
// 孩子当前的掌握状态。到期该复习的知识点 state=review,排在最前面。
async function buildQuestQueue(sessionId, dimension) {
  return fetchQueue(sessionId, dimension);
}

// 从第一个还没掌握的(或到期该复习的)知识点继续
function resolveStartQueueIndex(queue) {
  const index = queue.findIndex((item) => item.state !== 'mastered');
  return index === -1 ? queue.length : index;
}

// 按题目自己的 params 形状分派交互组件,不再按维度名称分派 —— 同一个
// 知识点(甚至同一个维度)的练习题池里可能混合普通点选题和分类归类题;
// 空间图形维度的测试关是 3D 拼装,但它的练习关其实是普通点选题,不能
// 简单按"维度是不是 spatial"判断,必须直接看这道题自己的 params 形状。
function resolvePuzzleComponent(puzzle) {
  const params = puzzle.params || {};
  if (params.type === 'categorize') return CategorySortPuzzle;
  if (params.type === 'poem') return PoemPuzzle;
  if (params.type === 'numeric') return NumericPuzzle;
  if (params.type === 'ordering') return OrderingPuzzle;
  if (params.type === 'multi') return MultiPuzzle;
  if (params.slots && params.pieces) return SpatialPuzzle3D;
  return PracticePuzzle;
}

// 练习连续答对几题算学会 / 复习连对几题算过关 —— 与后端保持一致,只用来画进度点
const MASTERY_STREAK = 3;
const REVIEW_STREAK = 2;
const CHEER_MS = 600;

// 这一程每一关的格子,按闯关顺序:一次就过=金色,试了几次才过=青瓷色,还没到=白色
function RoundSlots({ results, size, className }) {
  return Array.from({ length: size }, (_, i) => {
    const r = i < results.length ? (results[i] ? 'first' : 'retry') : 'empty';
    const color = { first: 'bg-qiji-gold', retry: 'bg-qiji-mint', empty: 'bg-white' }[r];
    return <span key={i} data-testid="round-slot" data-result={r} className={`rounded-full border border-black ${color} ${className}`} />;
  });
}

// 这一程的进度条
function RoundProgress({ label, results, size }) {
  return (
    <div className="flex w-full items-center gap-2" data-testid="round-progress">
      <span className="shrink-0 text-sm font-bold text-gray-700">{label}</span>
      <div className="flex flex-1 gap-1">
        <RoundSlots results={results} size={size} className="h-2.5 flex-1" />
      </div>
      <span className="shrink-0 text-sm text-gray-600">
        {Math.min(results.length, size)}/{size}
      </span>
    </div>
  );
}

function StreakDots({ done, total, label }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600" data-testid="streak">
      <span>{label}</span>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`h-4 w-4 rounded-full border-2 border-black ${i < done ? 'bg-qiji-mint' : 'bg-white'}`}
        />
      ))}
    </div>
  );
}

// cheerMs:答对后夸一句停留多久,测试里传 0
function QuestPage({ cheerMs = CHEER_MS }) {
  const navigate = useNavigate();
  const [questState] = useLocalStorage('huaxia_quest_state', null);
  // 从六艺书院点某门学科进来时只练这一门
  const [searchParams] = useSearchParams();
  const dimension = searchParams.get('dimension');

  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  // loading | test | learn | practice | cheer | grown | done | reviewed | round-done | all-done | error
  const [phase, setPhase] = useState('loading');
  const [skillPoint, setSkillPoint] = useState(null);
  const [testPuzzle, setTestPuzzle] = useState(null);
  const [practicePuzzles, setPracticePuzzles] = useState([]);
  const [practiceIndex, setPracticeIndex] = useState(0);
  const [hint, setHint] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [guardian, setGuardian] = useState(questState?.guardian || null);
  const [errorMsg, setErrorMsg] = useState('');
  const [isReview, setIsReview] = useState(false);
  const [streak, setStreak] = useState(0);
  const [cheer, setCheer] = useState('');
  const [nextReviewAt, setNextReviewAt] = useState(null);
  // 这一趟里新学会/复习过关的知识点排到的复习时间(队列里还是进来时的旧值)
  const [scheduledReviews, setScheduledReviews] = useState([]);
  // 每提交一次作答就 +1,当作练习题组件的 key 的一部分 —— 像"分类归类"这种
  // 带内部状态(已分类哪些物品)的组件,循环回同一道题重新作答时,题目对象
  // 本身没变(还是同一个 puzzle.id),不会自动重新挂载、内部状态也不会重置;
  // 强制换 key 触发重新挂载,保证每次作答都是一块干净的画布。
  const [attemptSeq, setAttemptSeq] = useState(0);
  const cheerTimer = useRef(null);

  const species = guardian?.species;
  const ageBand = questState?.ageBand;
  const copy = getCopy(ageBand);

  // 一程固定 roundSize 关(docs/TODO-improvement 3.x):掌握规则照常运行,一个知识点学会了就接着出下一个,
  // 做满一程停下来看成绩。答对一关、或探底关不论对错,都算过了一关;答错重试不算新的一关。
  // 计数放 ref 里,答题回调里同步读到最新值;roundView 只用来渲染。
  const roundSize = roundSizeFor(ageBand);
  const round = useRef(null);
  const wrongInSlot = useRef(false);
  const resumeAfterRound = useRef(null);
  // results:每一关是不是一次就过,按闯关顺序 —— 成绩页的格子要和第几关对得上
  const emptyView = { done: 0, firstTry: 0, results: [], learned: [] };
  const [roundView, setRoundView] = useState(emptyView);
  const freshRound = () => ({ id: newRoundId(), done: 0, firstTry: 0, results: [], learned: [] });
  if (round.current === null) round.current = freshRound();
  const trackRoundStart = () => track('round_start', { roundId: round.current.id, size: roundSize, dimension });
  const startRound = () => {
    round.current = freshRound();
    wrongInSlot.current = false;
    setRoundView(emptyView);
    trackRoundStart();
  };
  useEffect(() => {
    if (questState?.sessionId) trackRoundStart();
    // eslint-disable-next-line
  }, []);
  const completeSlot = (firstTry) => {
    const r = round.current;
    r.done += 1;
    if (firstTry) r.firstTry += 1;
    r.results.push(Boolean(firstTry));
    wrongInSlot.current = false;
    setRoundView({ done: r.done, firstTry: r.firstTry, results: [...r.results], learned: [...r.learned] });
  };
  const noteLearned = (name) => {
    if (!name) return;
    round.current.learned.push(name);
    setRoundView((v) => ({ ...v, learned: [...round.current.learned] }));
  };
  const roundFull = () => round.current.done >= roundSize;
  // 到了一程的终点就先看成绩,把「本来要做的下一步」存起来,点「再来一程」时接着做
  const continueOrEndRound = (next) => {
    if (!roundFull()) {
      next();
      return;
    }
    const r = round.current;
    track('round_complete', { roundId: r.id, size: roundSize, done: r.done, firstTry: r.firstTry, dimension });
    resumeAfterRound.current = next;
    setPhase('round-done');
  };
  const handleNextRound = () => {
    startRound();
    const next = resumeAfterRound.current;
    resumeAfterRound.current = null;
    next?.();
  };

  useEffect(() => () => clearTimeout(cheerTimer.current), []);

  // 埋点:题目出现的时刻(算作答耗时)和中途离开(放弃点)
  const puzzleShownAt = useRef(null);
  const exitInfo = useRef({});
  exitInfo.current = { phase, skillPointId: skillPoint?.id, isReview, streak };
  const activePuzzleId = phase === 'test' ? testPuzzle?.id : phase === 'practice' ? practicePuzzles[practiceIndex]?.id : null;

  useEffect(() => {
    if (!activePuzzleId) return;
    puzzleShownAt.current = Date.now();
    track('puzzle_view', { puzzleId: activePuzzleId, stage: phase, skillPointId: skillPoint?.id, isReview });
    // eslint-disable-next-line
  }, [activePuzzleId, attemptSeq]);

  useEffect(() => {
    if (phase === 'learn') track('capsule_view', { skillPointId: skillPoint?.id });
    // eslint-disable-next-line
  }, [phase === 'learn']);

  useEffect(() => {
    const reportExit = () => {
      const { phase: p, skillPointId, isReview: review, streak: st } = exitInfo.current;
      if (skillPointId && ['test', 'learn', 'practice', 'cheer'].includes(p)) {
        track('quest_exit', { phase: p, skillPointId, isReview: review, streak: st });
      }
    };
    window.addEventListener('pagehide', reportExit);
    return () => {
      window.removeEventListener('pagehide', reportExit);
      reportExit();
      flush({ keepalive: true });
    };
  }, []);

  const elapsedSincePuzzleShown = () =>
    puzzleShownAt.current ? Date.now() - puzzleShownAt.current : undefined;

  const startPractice = async (skillPointId, review) => {
    const list = await fetchPuzzles(skillPointId, 'practice');
    setPracticePuzzles(list.puzzles);
    // 复习时从随机一道开始,不要每次都是同一道
    setPracticeIndex(review && list.puzzles.length ? Math.floor(Math.random() * list.puzzles.length) : 0);
    setFeedback(null);
    setStreak(0);
    setPhase('practice');
  };

  const loadQueueItem = async (currentQueue, index) => {
    setPhase('loading');
    setErrorMsg('');
    const item = currentQueue[index];
    if (!item) {
      setPhase('all-done');
      return;
    }
    const review = item.state === 'review';
    setIsReview(review);
    track(review ? 'review_start' : 'quest_start', {
      skillPointId: item.skillPoint.id,
      dimension: item.skillPoint.dimension,
    });
    try {
      setSkillPoint(item.skillPoint);
      if (review) {
        // 复习:不再做诊断测试,直接练
        await startPractice(item.skillPoint.id, true);
        return;
      }
      const testList = await fetchPuzzles(item.skillPoint.id, 'test');
      setTestPuzzle(testList.puzzles[0]);
      setPhase('test');
    } catch {
      setPhase('error');
    }
  };

  useEffect(() => {
    if (!questState || !questState.sessionId) {
      navigate('/', { replace: true });
      return;
    }
    if (!questState.guardian?.species) {
      setPhase('error');
      setErrorMsg('还没有选定守护兽,请回到首页重新选择');
      return;
    }
    (async () => {
      try {
        const builtQueue = await buildQuestQueue(questState.sessionId, dimension);
        setQueue(builtQueue);
        const startIndex = resolveStartQueueIndex(builtQueue);
        setQueueIndex(startIndex);
        if (startIndex >= builtQueue.length) {
          setPhase('all-done');
          return;
        }
        await loadQueueItem(builtQueue, startIndex);
      } catch {
        setPhase('error');
      }
    })();
    // eslint-disable-next-line
  }, []);

  const handleTestComplete = async (answer) => {
    setSubmitting(true);
    try {
      const result = await submitAttempt(
        testPuzzle.id,
        questState.sessionId,
        answer,
        elapsedSincePuzzleShown(),
        round.current?.id
      );
      // 探底关不论对错都算过了一关(答错会先看小窍门,不重做这一关)
      completeSlot(result.correct);
      if (result.correct) {
        // 测试题答对:不弹胶囊夸一句,直接接着练 —— 一回合做完测试 + 练习才鼓励一次
        continueOrEndRound(handleLearnContinue);
      } else {
        setHint(result.hint || '换个角度再想想~');
        setPhase('learn');
      }
    } catch {
      setErrorMsg('网络好像开小差了,重新试一次吧');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLearnContinue = async () => {
    try {
      await startPractice(skillPoint.id, false);
    } catch {
      setErrorMsg('网络好像开小差了,重新试一次吧');
    }
  };

  const nextPractice = () => {
    // 练习题用完了但还没达标 —— 循环回第一题继续练
    setPracticeIndex((i) => (i + 1 < practicePuzzles.length ? i + 1 : 0));
  };

  const handlePracticeAnswer = async (answer) => {
    if (submitting || phase !== 'practice') return;
    setSubmitting(true);
    const currentPuzzle = practicePuzzles[practiceIndex];
    try {
      const result = await submitAttempt(
        currentPuzzle.id,
        questState.sessionId,
        answer,
        elapsedSincePuzzleShown(),
        round.current?.id
      );
      setAttemptSeq((n) => n + 1);
      if (!result.correct) {
        wrongInSlot.current = true;
        track('puzzle_retry', { puzzleId: currentPuzzle.id, skillPointId: skillPoint?.id, isReview });
        setStreak(0);
        setFeedback({ correct: false });
        return;
      }
      setFeedback(null);
      completeSlot(!wrongInSlot.current);
      if (isReview) {
        if (result.review?.passed) {
          noteLearned(skillPoint?.name);
          setNextReviewAt(result.review.nextReviewAt);
          setScheduledReviews((list) => [...list, result.review.nextReviewAt]);
          setPhase('reviewed');
          return;
        }
      } else if (result.mastery.state === 'mastered') {
        track('skill_mastered', { skillPointId: skillPoint?.id, guardianAdvanced: result.guardianAdvanced });
        noteLearned(skillPoint?.name);
        if (result.guardian) setGuardian(result.guardian);
        if (result.mastery.nextReviewAt) {
          setScheduledReviews((list) => [...list, result.mastery.nextReviewAt]);
        }
        setPhase(result.guardianAdvanced ? 'grown' : 'done');
        return;
      }
      // 答对了但还没达标:夸一句,停一下再出下一题
      setStreak((s) => (isReview ? s + 1 : result.mastery.practiceCount ?? s + 1));
      setCheer(copy.cheers[Math.floor(Math.random() * copy.cheers.length)]);
      setPhase('cheer');
      cheerTimer.current = setTimeout(() => {
        setCheer('');
        continueOrEndRound(() => {
          nextPractice();
          setPhase('practice');
        });
      }, cheerMs);
    } catch {
      setErrorMsg('网络好像开小差了,重新试一次吧');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQueueItemDone = () => {
    const nextIndex = queueIndex + 1;
    setQueueIndex(nextIndex);
    if (nextIndex < queue.length) {
      loadQueueItem(queue, nextIndex);
    } else {
      setPhase('all-done');
    }
  };

  if (phase === 'loading') {
    return <CenteredMessage text="正在打开书院的门..." />;
  }

  if (phase === 'error' || errorMsg) {
    return <CenteredMessage text={errorMsg || '路好像被云雾挡住了,刷新页面再试试吧'} isError />;
  }

  const TestComponent = testPuzzle && resolvePuzzleComponent(testPuzzle);
  const currentPractice = practicePuzzles[practiceIndex];
  const PracticeComponent = currentPractice && resolvePuzzleComponent(currentPractice);
  const hasNext = queueIndex + 1 < queue.length;
  // 最近一次还没到期的复习时间,用来告诉孩子什么时候再来
  const upcomingReview = [...queue.map((item) => item.nextReviewAt), ...scheduledReviews]
    .filter((t) => t && new Date(t) > new Date())
    .sort((a, b) => new Date(a) - new Date(b))[0];
  const streakTotal = Math.min(isReview ? REVIEW_STREAK : MASTERY_STREAK, practicePuzzles.length || 1);

  return (
    <div className="min-h-screen bg-qiji-paper px-4 py-10">
      <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-6">
        {phase !== 'all-done' && (
          <button
            type="button"
            onClick={() => navigate('/map')}
            className="self-start inline-flex min-h-[44px] items-center px-2 text-sm font-medium text-gray-600 underline"
          >
            ← 六艺书院
          </button>
        )}

        {['test', 'learn', 'practice', 'cheer', 'grown', 'done', 'reviewed'].includes(phase) && (
          <RoundProgress label={copy.roundLabel} results={roundView.results} size={roundSize} />
        )}

        {isReview && ['practice', 'cheer'].includes(phase) && (
          <p
            data-testid="review-banner"
            className="rounded-full border-2 border-black bg-qiji-gold px-4 py-1 text-sm font-bold text-gray-900"
          >
            🔁 复习一下:{skillPoint?.name}
          </p>
        )}

        {phase === 'test' && testPuzzle && TestComponent === SpatialPuzzle3D && (
          <>
            <h1 className="text-2xl font-extrabold text-gray-900">{testPuzzle.title}</h1>
            <SpatialPuzzle3D puzzle={testPuzzle} onComplete={handleTestComplete} />
          </>
        )}

        {phase === 'test' && testPuzzle && TestComponent !== SpatialPuzzle3D && (
          <TestComponent
            puzzle={testPuzzle}
            onAnswer={handleTestComplete}
            onComplete={handleTestComplete}
            feedback={null}
            disabled={submitting}
          />
        )}

        {phase === 'learn' && (
          <LearningCapsule
            hint={hint}
            explainer={testPuzzle?.params?.explainer}
            species={species}
            ageBand={ageBand}
            onContinue={() => continueOrEndRound(handleLearnContinue)}
          />
        )}

        {['practice', 'cheer'].includes(phase) && currentPractice && (
          <>
            <StreakDots
              done={streak}
              total={streakTotal}
              label={isReview ? copy.streakReview : copy.streakLearn}
            />
            {/* 夸奖占固定高度的一行,不遮挡题干、不推动布局 */}
            <div className="h-8 text-center" aria-live="polite">
              {phase === 'cheer' && (
                <p data-testid="cheer" role="status" className="text-lg font-bold text-green-700">
                  {speciesEmoji(species)} {cheer}
                </p>
              )}
            </div>
            <div className="relative w-full">
              <PracticeComponent
                key={`${currentPractice.id}-${attemptSeq}`}
                puzzle={currentPractice}
                onAnswer={handlePracticeAnswer}
                onComplete={handlePracticeAnswer}
                feedback={feedback}
                ageBand={ageBand}
                disabled={submitting || phase === 'cheer'}
              />
            </div>
          </>
        )}

        {phase === 'grown' && guardian && (
          <GuardianGrowth guardian={guardian} onContinue={() => setPhase('done')} />
        )}

        {(phase === 'done' || phase === 'reviewed') && (
          <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-black bg-white p-8 shadow-brutal">
            <span className="text-6xl">{phase === 'reviewed' ? '🔁' : '✨'}</span>
            <p className="text-center text-lg font-bold text-gray-800">
              {phase === 'reviewed' ? `${skillPoint?.name}复习过关!` : copy.doneTitle}
            </p>
            <p className="text-center text-sm text-gray-500">
              {phase === 'reviewed'
                ? copy.reviewedSub(describeNextReview(nextReviewAt))
                : copy.doneSub}
            </p>
            <button
              type="button"
              onClick={() => continueOrEndRound(handleQueueItemDone)}
              data-testid={hasNext ? 'next-dimension-button' : undefined}
              className={`rounded-lg px-6 py-3 text-base font-bold text-gray-900 border-2 border-black shadow-brutal-sm
                ${hasNext && !roundFull() ? 'bg-qiji-mint' : 'bg-qiji-gold'}`}
            >
              {roundFull() ? copy.roundSeeResult : hasNext ? '继续挑战下一关!' : '完成今天的冒险'}
            </button>
          </div>
        )}

        {phase === 'round-done' && (
          <div
            className="flex w-full flex-col items-center gap-4 rounded-2xl border-2 border-black bg-white p-8 shadow-brutal"
            data-testid="round-done-screen"
          >
            {(() => {
              const medal = medalFor(roundView.firstTry, roundView.done);
              return (
                <div className="flex flex-col items-center gap-1" data-testid="round-medal" data-medal={medal.key}>
                  <span className="text-7xl leading-none">{medal.icon}</span>
                  <p className="text-sm text-gray-500">{copy.roundDoneTitle}</p>
                  <p className="text-2xl font-extrabold text-gray-900">{copy.medals[medal.key]}</p>
                  <p className="text-center text-sm text-gray-600">{copy.medalSub[medal.key]}</p>
                </div>
              );
            })()}
            <div className="flex flex-col items-center gap-1.5">
              <div className="flex gap-1">
                <RoundSlots results={roundView.results} size={roundSize} className="h-3 w-5" />
              </div>
              <div className="flex gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="h-2.5 w-4 rounded-full border border-black bg-qiji-gold" />
                  {copy.roundLegendFirst}
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2.5 w-4 rounded-full border border-black bg-qiji-mint" />
                  {copy.roundLegendRetry}
                </span>
              </div>
            </div>
            <p className="text-center text-base font-bold text-gray-800" data-testid="round-first-try">
              {copy.roundFirstTry(roundView.firstTry, roundView.done)}
            </p>
            {roundView.learned.length > 0 && (
              <p className="text-center text-sm text-gray-600" data-testid="round-learned">
                {copy.roundLearned}:{roundView.learned.join('、')}
              </p>
            )}
            <button
              type="button"
              data-testid="next-round-button"
              onClick={handleNextRound}
              className="rounded-lg bg-qiji-gold px-6 py-3 text-base font-bold text-gray-900 border-2 border-black shadow-brutal-sm"
            >
              {copy.roundAgain}
            </button>
            <button
              type="button"
              onClick={() => navigate('/map')}
              className="inline-flex min-h-[44px] items-center px-2 text-sm text-gray-600 underline"
            >
              回到六艺书院
            </button>
          </div>
        )}

        {phase === 'all-done' && (
          <div
            className="flex flex-col items-center gap-4 rounded-2xl border-2 border-black bg-white p-8 shadow-brutal"
            data-testid="all-done-screen"
          >
            <span className="text-6xl">🏆</span>
            <p className="text-center text-lg font-bold text-gray-800">
              {dimension ? '这门学科都练完啦!去书院看看别的吧~' : '今天的本领都练完啦!'}
            </p>
            {roundView.done > 0 && (
              <p className="text-center text-sm font-bold text-gray-700" data-testid="round-first-try">
                {copy.roundFirstTry(roundView.firstTry, roundView.done)}
              </p>
            )}
            {upcomingReview && (
              <p className="text-center text-sm text-gray-600" data-testid="next-review-hint">
                {speciesEmoji(species)} {describeNextReview(upcomingReview)}记得回来复习,学过的才不会忘
              </p>
            )}
            <button
              type="button"
              onClick={() => navigate('/map')}
              className="rounded-lg bg-qiji-gold px-6 py-3 text-base font-bold text-gray-900
                         border-2 border-black shadow-brutal-sm"
            >
              回到六艺书院
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CenteredMessage({ text, isError }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-qiji-paper px-4">
      <p className={`text-lg ${isError ? 'text-red-500' : 'text-gray-500'}`} role={isError ? 'alert' : undefined}>
        {text}
      </p>
    </div>
  );
}

export default QuestPage;
